import { PermissionEnum } from '@Application/Security/Roles';
import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import PermissionGroup from '@UI/Pages/Administration/Roles/RoleCard/PermissionGroup';
import { setRoleToDelete } from '@UI/Pages/Administration/Roles/Signals/roleToDeleteSignal';
import {
	duplicateRoleInSidebar,
	editRoleInSidebar,
} from '@UI/Pages/Administration/Roles/Signals/roleSidebarSignal';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import type PermissionCatalogView from '@UI/Store/PermissionCatalog/Views/PermissionCatalogView';
import { css } from '@emotion/react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Button, Tag } from '@verclam/ui-kit';

const headerRowStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
`;

const headerTextStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.3125rem;
`;

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const metaStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkFaint};
`;

const warningStyle = css`
	margin: 0;
	padding: 0.75rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const DOSSIERS_CATEGORY = 'Dossiers';
const DOSSIERS_CATEGORY_HINT =
	"L'accès aux bénéficiaires et aux autres types de dossier se règle dans Types de dossier.";

type Props = {
	readonly role: RoleView;
	readonly catalog: PermissionCatalogView;
};

/**
 * The single role selected by `RoleTabs`: identity, badge, an edit action offered only when the role
 * is not a system default (`canEdit`), an always-available duplicate action, and an optional delete
 * action, then warnings and the catalogue grouped by category. A system role is never modified from
 * the interface — its warning states the rule and points to duplication as the way to adjust its
 * rights, so "Dupliquer" stays outside `canEdit` and shows on every role, system or not.
 * `SYSTEM_CONFIG` is dropped from every group (and the group itself if that was its only permission) —
 * it never appears on this screen, whatever the role.
 */
const RoleCard: React.FC<Props> = ({ role, catalog }) => {
	const canEdit = !role.isSystem;
	const canDelete = !role.isSystem && role.assignedUserCount === 0;

	const handleEditClick = React.useCallback(() => {
		editRoleInSidebar(role.id);
	}, [role.id]);

	const handleDuplicateClick = React.useCallback(() => {
		duplicateRoleInSidebar(role.id);
	}, [role.id]);

	const handleDeleteClick = React.useCallback(() => {
		setRoleToDelete(role.id);
	}, [role.id]);

	const groups = React.useMemo(
		() =>
			Array.from(catalog.byCategory.entries())
				.map(
					([category, permissions]) =>
						[
							category,
							permissions.filter(
								(permission) => permission.value !== PermissionEnum.SYSTEM_CONFIG,
							),
						] as const,
				)
				.filter(([, permissions]) => permissions.length > 0),
		[catalog],
	);

	return (
		<GridCard layout="stacked" rowGap="1.375rem">
			<div css={headerRowStyle}>
				<div css={headerTextStyle}>
					<div css={titleRowStyle}>
						<h2 css={titleStyle}>{role.label}</h2>
						<Tag
							value={role.isSystem ? 'Rôle par défaut' : 'Rôle personnalisé'}
							severity={role.isSystem ? 'info' : 'secondary'}
							rounded
						/>
					</div>
					{role.description !== null ? <p css={descriptionStyle}>{role.description}</p> : null}
					{role.assignedUserCount > 0 ? (
						<p css={metaStyle}>{role.assignedUserCount} utilisateur(s) portent ce rôle.</p>
					) : null}
				</div>
				<div css={actionsRowStyle}>
					{canEdit ? (
						<Button
							type="button"
							text
							icon={<Pencil size={16} />}
							label="Modifier"
							onClick={handleEditClick}
						/>
					) : null}
					<Button
						type="button"
						text
						icon={<Copy size={16} />}
						label="Dupliquer"
						onClick={handleDuplicateClick}
					/>
					{canDelete ? (
						<Button
							type="button"
							severity={'danger'}
							text
							icon={<Trash2 size={16} />}
							label="Supprimer le rôle"
							onClick={handleDeleteClick}
						/>
					) : null}
				</div>
			</div>
			{role.isSystem ? (
				<p css={warningStyle}>
					Un rôle par défaut ne peut être ni modifié ni supprimé. Pour un jeu de droits différent,
					dupliquez-le et ajustez la copie.
				</p>
			) : null}
			{role.permissions.length === 0 ? (
				<p css={warningStyle}>Ce rôle n’accorde aucun droit. Ses porteurs ne pourront rien faire.</p>
			) : null}
			{groups.map(([category, permissions]) => (
				<PermissionGroup
					key={category}
					title={category}
					permissions={permissions}
					role={role}
					hint={category === DOSSIERS_CATEGORY ? DOSSIERS_CATEGORY_HINT : undefined}
				/>
			))}
		</GridCard>
	);
};

export default RoleCard;
